import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, {
  setAuthToken,
  getAuthToken,
  setRefreshToken,
  attemptRefresh,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // If no access token, try silent refresh via refresh_token before giving up.
    if (!getAuthToken()) {
      const newAccess = await attemptRefresh();
      if (!newAccess) {
        setUser(false);
        setLoading(false);
        return;
      }
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.error("Auth refresh failed:", err);
      setAuthToken(null);
      setRefreshToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) setAuthToken(data.access_token);
    if (data?.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.access_token) setAuthToken(data.access_token);
    if (data?.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.error("Logout API call failed:", err); }
    setAuthToken(null);
    setRefreshToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
